import { Offer } from 'src/app/feature-modules/offers/models/offers.model';
import { createSelector } from '@ngrx/store';
import { selectCustomerAttributes } from 'src/app/feature-modules/customer-attributes/store/customer-attributes.selector';
import {
  selectAccounts,
  selectBechError,
  selectCreditCardError,
  selectGatewayError,
  selectLoanError,
} from 'src/app/feature-modules/accounts/store/accounts.selector';
import { selectHubOffers, selectOfferOffers } from 'src/app/feature-modules/offers/store/selectors/offers.selectors';
import { selectHubFeatureFlags, selectOfferFeatureFlags } from 'src/app/store/selectors/feature-flag.selector';
import { HeaderNavViewModelProps, NavItem } from './header-nav.model';
import { pipe } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CustomerAttributesState } from 'src/app/feature-modules/customer-attributes/models/customer-attributes.model';
import { getFeatureFlagEnabledByName } from 'src/app/utils/feature-flag.util';
import { mapBefhNavItem, mapCardNavItem, mapLoanNavItem, mapOfferNavItem } from './nav-item.util';
import { selectProfilePaperless } from '../../feature-modules/profile/store/selectors/profile.selectors';
import { ProfilePaperlessNormalized } from '../../feature-modules/profile/models/profile-paperless.model';
import { SelfServiceableAccounts } from '../../feature-modules/accounts/models/accounts.model';

// View model selector
export const selectHeaderNavViewModelProps = createSelector(
  selectCustomerAttributes,
  selectAccounts,
  selectProfilePaperless,
  selectLoanError,
  selectCreditCardError,
  selectBechError,
  selectGatewayError,
  selectHubOffers,
  selectOfferOffers,
  selectHubFeatureFlags,
  selectOfferFeatureFlags,
  (
    attrs,
    accounts,
    profilePaperless,
    loanError,
    cardError,
    befhError,
    gatewayError,
    hubOffers,
    offerOffers,
    hubFlags,
    offerFlags
  ) => {
    const props: HeaderNavViewModelProps = {
      customerAttributes: attrs,
      accounts,
      profilePaperless,
      offers: hubOffers || offerOffers,
      featureFlags: hubFlags || offerFlags,
      profileIncome: attrs.customerProfileIncome,
      loanError,
      cardError,
      befhError,
      gatewayError,
    };
    return props;
  }
);

export const filterFalsyViewModelProps = () =>
  pipe(
    filter((props: HeaderNavViewModelProps) => {
      const { customerAttributes, accounts, offers, featureFlags, profileIncome } = props;
      return !!customerAttributes && !!accounts && !!offers && !!featureFlags && !!profileIncome;
    })
  );

// View model
export class HeaderNavViewModel {
  accounts: SelfServiceableAccounts;
  profilePaperless: ProfilePaperlessNormalized;
  customerAttributes: CustomerAttributesState;
  offers: Offer[] = [];

  hasOfferEnabled: boolean;

  loanError: string;
  cardError: string;
  bechError: string;
  gatewayError: string;

  customerFirstName: string;
  customerLastName: string;
  incomeRefreshRequired: boolean;
  hasPastDueAccount: boolean;

  constructor(vmProps: HeaderNavViewModelProps) {
    const {
      customerAttributes: attributes,
      accounts,
      profilePaperless,
      offers,
      featureFlags,
      profileIncome,
      loanError,
      cardError,
      befhError,
      gatewayError,
    } = vmProps;

    this.accounts = accounts;
    this.profilePaperless = profilePaperless;
    this.customerAttributes = attributes;
    this.offers = offers;

    this.hasOfferEnabled = getFeatureFlagEnabledByName(featureFlags, 'AWEE311OfferNav');
    this.customerFirstName = attributes.trustedContactFirstName;
    this.customerLastName = attributes.trustedContactLastName;
    this.incomeRefreshRequired = profileIncome.isCustomerIncomeAttributeRefreshRequired;
    this.hasPastDueAccount = !!accounts?.pastDueLoanAccount;
    this.loanError = loanError;
    this.cardError = cardError;
    this.bechError = befhError;
    this.gatewayError = gatewayError;
  }

  getHasLoanItem() {
    const defaultAddressState =
      (this.customerAttributes.trustedContactAddress || []).find((addr) => addr.AddressType === 'Default')?.State || '';
    const isExcludedState = defaultAddressState === 'NV' || defaultAddressState === 'NE';

    if (this.accounts.loanAccounts.length || !isExcludedState) {
      return true;
    }
    return false;
  }

  getHasCardItem() {
    if (this.accounts.cardAccounts.length) {
      return true;
    }
    return this.offers.some((offer) => offer.productType === 'CREDIT_CARD');
  }

  getNavItems(): NavItem[] {
    const items: NavItem[] = [];
    // Nav items (in order)
    // Home
    items.push({
      title: 'Home',
      type: 'home',
      isActive: false,
      isDisabled: false,
      isExternalLink: false,
      isSlideNavDelay: false,
    });

    // Loan
    if (this.getHasLoanItem()) {
      items.push(mapLoanNavItem(this.accounts, this.profilePaperless, this.loanError, this.hasPastDueAccount));
    }

    // Card
    if (this.getHasCardItem()) {
      items.push(mapCardNavItem(this.accounts, this.offers, this.cardError));
    }

    // Befh
    items.push(mapBefhNavItem(this.accounts, this.bechError));

    // Offers
    if (this.hasOfferEnabled) {
      items.push(mapOfferNavItem(this.offers));
    }
    return items;
  }
}
